import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";

import {
    setActivity,
    setBanner,
    setOffers,
    setActions,
    setMyOrder,
    setAdvertising,
    setMiscellaneous,
    setPaymentOptions,
    setServerCallOptions,
} from "../../../../../redux/scanToSlice";
import { RootState } from "../../../../../redux/store";
import { ShopRowData } from "../types";

import { Activite } from "./Activite";
import { Banniere } from "./Banniere";
import { Offres } from "./Offres";
import { Actions } from "./Actions";
import { MaCommande } from "./MaCommande";
import { Publicites } from "./Publicites";
import { PiedDePage } from "./PiedDePage";
import { Paiement } from "./Paiement";
import { AppelServeur } from "./AppelServeur";
import {
    SCAN_TO_DEFAULT_MAIN_COLOR,
    ACTIVITY_TAB_INDEX,
} from "./shared/constants";
import { flattenOffersForPreview } from "./View/flattenOffersForPreview";
import { SharedPreviewPanel } from "./SharedPreviewPanel";

import "./index.css";

// Styled components for collapsible sidebar
const ToggleButton = styled.button<{ mainColor: string }>`
    width: 100%;
    height: 48px;
    background-color: rgba(0, 0, 0, 0.1);
    border: none;
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: rgba(0, 0, 0, 0.2);
    }

    svg {
        width: 24px;
        height: 24px;
        transition: transform 0.3s ease;
    }
`;

const NavLinkContent = styled.div<{ isCollapsed: boolean }>`
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: ${({ isCollapsed }) =>
        isCollapsed ? "center" : "flex-start"};
    width: 100%;
    padding: 0 ${({ isCollapsed }) => (isCollapsed ? "0" : "8px")};

    svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
    }

    span {
        white-space: nowrap;
        opacity: ${({ isCollapsed }) => (isCollapsed ? "0" : "1")};
        transform: ${({ isCollapsed }) =>
            isCollapsed ? "translateX(-10px)" : "translateX(0)"};
        transition: opacity 0.2s ease, transform 0.2s ease;
        display: ${({ isCollapsed }) => (isCollapsed ? "none" : "inline")};
    }
`;

const TooltipWrapper = styled.div`
    position: relative;
    width: 100%;
`;

// Forms (1fr) + the single shared preview (480px). Mirrors the per-tab
// Container grid the previews used to live in; under 992px the preview
// collapses out of flow into a fixed slide-in panel (see SharedPreviewPanel).
// On the Activity tab there is no preview, so the form spans the full width.
const ContentArea = styled.div<{ $showPreview: boolean }>`
    display: grid;
    grid-template-columns: ${({ $showPreview }) =>
        $showPreview ? "1fr 480px" : "1fr"};
    gap: 30px;
    height: 100%;
    overflow: hidden;

    @media (max-width: 1200px) {
        grid-template-columns: ${({ $showPreview }) =>
            $showPreview ? "1fr 400px" : "1fr"};
        gap: 20px;
    }

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
        gap: 0;
    }
`;

type TabsMenuType = {
    title: string;
    content: React.ReactNode;
    customClassName: string;
    icon: React.ReactNode;
};

type PropsType = {
    rowData?: ShopRowData;
    pendingFilesRef: React.MutableRefObject<Map<string, File>>;
    deletedImagesRef: React.MutableRefObject<Set<string>>;
};

// ─── Tab icons (module-level — no closure, safe to hoist) ─────
const ActivityIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z" />
    </svg>
);
const BannerIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14zm-10-7h9v6h-9z" />
    </svg>
);
const OffersIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z" />
    </svg>
);
const ActionsIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-2 10h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
    </svg>
);
const OrderIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
    </svg>
);
const AdvertisingIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 16v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-5 0h-2v-2h2v2zm0-4h-2V8h2v4zm-1 10c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2z" />
    </svg>
);
const SettingsIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
    </svg>
);
const PaymentIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
    </svg>
);
const ServerCallIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
    </svg>
);

// Create context for sharing selected activity and preview data across tabs
type ScanToContextType = {
    selectedActivity: string;
    setSelectedActivity: (activity: string) => void;
    appName: string;
    setAppName: (name: string) => void;
    // Shared preview data for cross-tab sync
    sharedBannerData: any;
    setSharedBannerData: (data: any) => void;
    sharedOffresData: any;
    setSharedOffresData: (data: any) => void;
    sharedActionsData: any;
    setSharedActionsData: (data: any) => void;
    sharedPublicitesData: any;
    setSharedPublicitesData: (data: any) => void;
    sharedCommandeData: any;
    setSharedCommandeData: (data: any) => void;
    sharedDiversData: any;
    setSharedDiversData: (data: any) => void;
    sharedPaiementData: any;
    setSharedPaiementData: (data: any) => void;
    sharedAppelServeurData: any;
    setSharedAppelServeurData: (data: any) => void;
    // ─── Per-modal-instance image registries (refs from parent) ───
    // Components call these helpers to track uploads/deletes scoped
    // to the current modal lifecycle. handleSave (in parent) reads
    // the underlying refs to build the multipart payload.
    registerPendingFile: (blobUrl: string, file: File) => void;
    removePendingFile: (blobUrl: string) => void;
    registerDeletedImage: (filename: string) => void;
    // Single shared BroadcastChannel for the external preview window. Each tab
    // posts updates through this ref; the channel itself is created once and
    // closed when the modal unmounts.
    broadcastChannelRef: React.MutableRefObject<BroadcastChannel | null>;
};

export const ScanToContext = React.createContext<ScanToContextType>({
    selectedActivity: "fastfood",
    setSelectedActivity: () => {},
    appName: "eat",
    setAppName: () => {},
    sharedBannerData: null,
    setSharedBannerData: () => {},
    sharedOffresData: null,
    setSharedOffresData: () => {},
    sharedActionsData: null,
    setSharedActionsData: () => {},
    sharedPublicitesData: null,
    setSharedPublicitesData: () => {},
    sharedCommandeData: null,
    setSharedCommandeData: () => {},
    sharedDiversData: null,
    setSharedDiversData: () => {},
    sharedPaiementData: null,
    setSharedPaiementData: () => {},
    sharedAppelServeurData: null,
    setSharedAppelServeurData: () => {},
    registerPendingFile: () => {},
    removePendingFile: () => {},
    registerDeletedImage: () => {},
    broadcastChannelRef: { current: null },
});

export function ModalContentWrapper({
    rowData,
    pendingFilesRef,
    deletedImagesRef,
}: PropsType): JSX.Element {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const mainColor =
        useSelector((state: RootState) => state.global.infoUser.maincolor) ||
        SCAN_TO_DEFAULT_MAIN_COLOR;

    // ─── Read initial values from Redux ──────────────────
    const scanToRedux = useSelector((state: RootState) => state.scanTo);

    const [activeTab, setActiveTab] = React.useState<number>(0);
    const [isCollapsed, setIsCollapsed] = React.useState<boolean>(false);

    // ─── Shared preview data is now read straight from Redux ───
    // Children that previously called `setSharedXData(payload)` from context
    // are now wired to dispatchers below, so writes go directly to the store
    // instead of round-tripping through `useState` + a sync effect.
    const selectedActivity =
        scanToRedux?.activity?.selectedActivity || "fastfood";
    const appName = scanToRedux?.activity?.appName || "eat";
    const sharedBannerData = scanToRedux?.banner || null;
    // setOffers reducer reshapes the broadcast's runtime (flat, translated,
    // active-filtered) offers array into a { Mobile, Desktop } persistence
    // shape — overwriting the flat array. So the value read back here has
    // `.offers = { Mobile: OfferItem[], Desktop: OfferItem[] }` where each
    // OfferItem still has per-language `title` / `description` objects.
    // Rebuild the flat translated array the preview expects (filtered by
    // active, sorted by order, translated to `selectedOffersLang`), so
    // other rubrics see the user's edits instead of falling through to
    // OffersSection's hardcoded fallback cards.
    const sharedOffresData = React.useMemo(() => {
        const raw = scanToRedux?.offers;
        if (!raw) return null;
        return { ...raw, offers: flattenOffersForPreview(raw) };
    }, [scanToRedux?.offers]);
    const sharedActionsData = scanToRedux?.actions || null;
    const sharedPublicitesData = scanToRedux?.advertising || null;
    const sharedCommandeData = scanToRedux?.myOrder || null;
    const sharedDiversData = scanToRedux?.miscellaneousSettings || null;
    const sharedPaiementData = scanToRedux?.paymentOptions || null;
    const sharedAppelServeurData = scanToRedux?.serverCallOptions || null;

    // ─── Reset per-modal upload/delete registries on mount ───
    // Prevents stale entries from a previous (cancelled) modal session
    // leaking into the next save when the user reopens the modal.
    React.useEffect(() => {
        pendingFilesRef.current.clear();
        deletedImagesRef.current.clear();
    }, [pendingFilesRef, deletedImagesRef]);

    // ─── Centralized BroadcastChannel for the external preview window ───
    // One channel for the whole modal: tabs share it through context instead
    // of each opening their own. Wrapped in try/catch because BroadcastChannel
    // is unavailable in some test/SSR environments.
    const broadcastChannelRef = React.useRef<BroadcastChannel | null>(null);
    React.useEffect(() => {
        if (typeof BroadcastChannel === "undefined") return;
        try {
            broadcastChannelRef.current = new BroadcastChannel(
                "scan-to-preview-channel"
            );
        } catch {
            broadcastChannelRef.current = null;
        }
        return () => {
            broadcastChannelRef.current?.close();
            broadcastChannelRef.current = null;
        };
    }, []);

    const registerPendingFile = React.useCallback(
        (blobUrl: string, file: File) => {
            pendingFilesRef.current.set(blobUrl, file);
        },
        [pendingFilesRef]
    );

    const removePendingFile = React.useCallback(
        (blobUrl: string) => {
            pendingFilesRef.current.delete(blobUrl);
        },
        [pendingFilesRef]
    );

    const registerDeletedImage = React.useCallback(
        (filename: string) => {
            if (filename) deletedImagesRef.current.add(filename);
        },
        [deletedImagesRef]
    );

    // ─── Context setters dispatch directly to Redux ──────
    // No more pass-through state + sync effect: each setter is a thin wrapper
    // around the corresponding slice action, so children that call
    // `setSharedXData(payload)` from context update the store synchronously.
    // Dispatch only the field that changed — the setActivity reducer does
    // Object.assign onto state.activity, so a partial payload merges
    // correctly without disturbing the other field. Sending the full
    // { selectedActivity, appName } object from each setter caused a
    // stale-closure overwrite when handleSelectActivity calls both setters
    // back-to-back (the second call would clobber the first with a stale
    // selectedActivity captured before the first dispatch landed).
    const setSelectedActivity = React.useCallback(
        (value: string) => {
            dispatch(setActivity({ selectedActivity: value as any }));
        },
        [dispatch]
    );

    const setAppName = React.useCallback(
        (value: string) => {
            dispatch(setActivity({ appName: value }));
        },
        [dispatch]
    );

    const setSharedBannerData = React.useCallback(
        (data: any) => {
            if (data) dispatch(setBanner(data));
        },
        [dispatch]
    );

    const setSharedOffresData = React.useCallback(
        (data: any) => {
            if (data) dispatch(setOffers(data));
        },
        [dispatch]
    );

    const setSharedActionsData = React.useCallback(
        (data: any) => {
            if (data) dispatch(setActions(data));
        },
        [dispatch]
    );

    const setSharedPublicitesData = React.useCallback(
        (data: any) => {
            if (data) dispatch(setAdvertising(data));
        },
        [dispatch]
    );

    const setSharedCommandeData = React.useCallback(
        (data: any) => {
            if (data) dispatch(setMyOrder(data));
        },
        [dispatch]
    );

    const setSharedDiversData = React.useCallback(
        (data: any) => {
            if (data) dispatch(setMiscellaneous(data));
        },
        [dispatch]
    );

    const setSharedPaiementData = React.useCallback(
        (data: any) => {
            if (data) dispatch(setPaymentOptions(data));
        },
        [dispatch]
    );

    const setSharedAppelServeurData = React.useCallback(
        (data: any) => {
            if (data) dispatch(setServerCallOptions(data));
        },
        [dispatch]
    );

    const tabs: TabsMenuType[] = [
        {
            title: t("Activity"),
            content: <Activite rowData={rowData} />,
            customClassName: "scan-to_sid_activite",
            icon: <ActivityIcon />,
        },
        {
            title: t("Banner"),
            content: <Banniere rowData={rowData} />,
            customClassName: "scan-to_sid_banniere",
            icon: <BannerIcon />,
        },
        {
            title: t("Offers"),
            content: <Offres rowData={rowData} />,
            customClassName: "scan-to_sid_offres",
            icon: <OffersIcon />,
        },
        {
            title: t("Actions"),
            content: <Actions rowData={rowData} />,
            customClassName: "scan-to_sid_actions",
            icon: <ActionsIcon />,
        },
        {
            title: t("My order"),
            content: <MaCommande rowData={rowData} />,
            customClassName: "scan-to_sid_maCommande",
            icon: <OrderIcon />,
        },
        {
            title: t("Advertising"),
            content: <Publicites rowData={rowData} />,
            customClassName: "scan-to_sid_publicites",
            icon: <AdvertisingIcon />,
        },
        {
            title: t("Miscellaneous settings"),
            content: <PiedDePage rowData={rowData} />,
            customClassName: "scan-to_sid_piedDePage",
            icon: <SettingsIcon />,
        },
        {
            title: t("Payment options"),
            content: <Paiement rowData={rowData} />,
            customClassName: "scan-to_sid_paiement",
            icon: <PaymentIcon />,
        },
        {
            title: t("Server call options"),
            content: <AppelServeur rowData={rowData} />,
            customClassName: "scan-to_sid_appelServeur",
            icon: <ServerCallIcon />,
        },
    ];

    function toggleNav(tab: number) {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    }

    const contextValue = React.useMemo(
        () => ({
            selectedActivity,
            setSelectedActivity,
            appName,
            setAppName,
            sharedBannerData,
            setSharedBannerData,
            sharedOffresData,
            setSharedOffresData,
            sharedActionsData,
            setSharedActionsData,
            sharedPublicitesData,
            setSharedPublicitesData,
            sharedCommandeData,
            setSharedCommandeData,
            sharedDiversData,
            setSharedDiversData,
            sharedPaiementData,
            setSharedPaiementData,
            sharedAppelServeurData,
            setSharedAppelServeurData,
            registerPendingFile,
            removePendingFile,
            registerDeletedImage,
            broadcastChannelRef,
        }),
        [
            selectedActivity,
            setSelectedActivity,
            appName,
            setAppName,
            sharedBannerData,
            setSharedBannerData,
            sharedOffresData,
            setSharedOffresData,
            sharedActionsData,
            setSharedActionsData,
            sharedPublicitesData,
            setSharedPublicitesData,
            sharedCommandeData,
            setSharedCommandeData,
            sharedDiversData,
            setSharedDiversData,
            sharedPaiementData,
            setSharedPaiementData,
            sharedAppelServeurData,
            setSharedAppelServeurData,
            registerPendingFile,
            removePendingFile,
            registerDeletedImage,
        ]
    );

    return (
        <ScanToContext.Provider value={contextValue}>
            <div
                className="d-grid__clz h-100"
                data-testid="scan-to-modal-container"
                style={{
                    gridTemplateColumns: isCollapsed ? "60px 1fr" : "220px 1fr",
                    transition: "grid-template-columns 0.3s ease",
                }}
            >
                <Nav
                    tabs
                    className="d-flex flex-column scan-to-sidebar__clz"
                    style={{ backgroundColor: mainColor }}
                    data-testid="scan-to-modal-sidebar-nav"
                >
                    {/* Toggle Button */}
                    <ToggleButton
                        data-testid="scan-to-modal-sidebar-toggle-btn"
                        mainColor={mainColor}
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        title={
                            isCollapsed ? t("Expand menu") : t("Collapse menu")
                        }
                        aria-label={
                            isCollapsed ? t("Expand menu") : t("Collapse menu")
                        }
                        aria-expanded={!isCollapsed}
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            style={{
                                transform: isCollapsed
                                    ? "rotate(180deg)"
                                    : "rotate(0deg)",
                            }}
                        >
                            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
                        </svg>
                    </ToggleButton>

                    {/* Menu Items */}
                    {tabs.map((element, index) => {
                        return (
                            <TooltipWrapper key={index}>
                                <NavItem
                                    data-testid={`scan-to-modal-tab-item-${index}`}
                                    className={classNames(
                                        "pb-1 scan-to-menu-item__clz",
                                        {
                                            "active scan-to-menu-item-active__clz":
                                                activeTab === index,
                                        },
                                        element.customClassName
                                    )}
                                    title={isCollapsed ? element.title : ""}
                                >
                                    <NavLink
                                        data-testid={`scan-to-modal-tab-link-${index}`}
                                        className={classNames(
                                            "border-0 scan-to-nav-link__clz",
                                            {
                                                "active scan-to-nav-link-active__clz":
                                                    activeTab === index,
                                            }
                                        )}
                                        onClick={() => {
                                            toggleNav(index);
                                        }}
                                        style={{
                                            padding: isCollapsed
                                                ? "12px 0"
                                                : "12px",
                                        }}
                                    >
                                        <NavLinkContent
                                            isCollapsed={isCollapsed}
                                        >
                                            {element.icon}
                                            <span>{element.title}</span>
                                        </NavLinkContent>
                                    </NavLink>
                                </NavItem>
                            </TooltipWrapper>
                        );
                    })}
                </Nav>

                <ContentArea $showPreview={activeTab !== ACTIVITY_TAB_INDEX}>
                    <TabContent
                        activeTab={activeTab}
                        data-testid="scan-to-modal-tab-content"
                        style={{
                            overflowY: "auto",
                            height: "100%",
                        }}
                    >
                        {tabs.map((element, index) => {
                            return (
                                <TabPane
                                    tabId={index}
                                    key={index}
                                    className="h-100"
                                    data-testid={`scan-to-modal-tab-pane-${index}`}
                                >
                                    {element.content}
                                </TabPane>
                            );
                        })}
                    </TabContent>

                    {/* One preview, shared by every tab — single source of
                        truth for language/direction and section data. The
                        Activity setup tab has no preview. */}
                    {activeTab !== ACTIVITY_TAB_INDEX && (
                        <SharedPreviewPanel activeTab={activeTab} />
                    )}
                </ContentArea>
            </div>
        </ScanToContext.Provider>
    );
}
